import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Send, Loader2, X, Phone, MessageSquare, Mail } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { CLEANING_TYPES } from "../data/business";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_PHOTOS = 4;
const MAX_PHOTO_BYTES = 2.5 * 1024 * 1024; // 2.5MB per file

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function QuoteForm({ defaultCity = "", compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: defaultCity,
    cleaning_type: "",
    preferred_contact: "phone",
    message: "",
  });
  const [photos, setPhotos] = useState([]); // [{name, dataUrl}]
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const slotsLeft = MAX_PHOTOS - photos.length;
    if (slotsLeft <= 0) {
      toast.error(`You can upload up to ${MAX_PHOTOS} photos.`);
      return;
    }
    const accepted = files.slice(0, slotsLeft);
    const oversize = accepted.find((f) => f.size > MAX_PHOTO_BYTES);
    if (oversize) {
      toast.error("Each photo must be under 2.5 MB.");
      return;
    }
    const encoded = await Promise.all(
      accepted.map(async (f) => ({ name: f.name, dataUrl: await fileToBase64(f) }))
    );
    setPhotos((p) => [...p, ...encoded]);
    e.target.value = "";
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    if (!form.cleaning_type) {
      toast.error("Please choose a type of cleaning.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        photos: photos.map((p) => p.dataUrl),
      });
      toast.success("Thank you — your quote request has been received. We will contact you soon.");
      setForm({
        name: "",
        phone: "",
        email: "",
        address: defaultCity,
        cleaning_type: "",
        preferred_contact: "phone",
        message: "",
      });
      setPhotos([]);
    } catch (err) {
      console.error("[QuoteForm] submit failed:", err);
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Unknown error";
      toast.error(`Could not send your request: ${detail}. Please call or text us at 219-455-1453.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className={`bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8 ${compact ? "" : ""}`}
      data-testid="quote-form"
    >
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900">
            Get a Free Cleaning Quote
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Fast response — usually same day. Final pricing depends on size, condition, frequency and scope.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="qf-name">Name *</Label>
          <Input
            id="qf-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your name"
            data-testid="qf-name"
            required
          />
        </div>
        <div>
          <Label htmlFor="qf-phone">Phone *</Label>
          <Input
            id="qf-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(219) 555-1234"
            data-testid="qf-phone"
            required
          />
        </div>
        <div>
          <Label htmlFor="qf-email">Email</Label>
          <Input
            id="qf-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
            data-testid="qf-email"
          />
        </div>
        <div>
          <Label htmlFor="qf-address">Address or City</Label>
          <Input
            id="qf-address"
            value={form.address}
            onChange={(e) => update("address", e.target.value)}
            placeholder="Street, city, ZIP"
            data-testid="qf-address"
          />
        </div>
        <div>
          <Label htmlFor="qf-cleaning-type">Type of Cleaning *</Label>
          <Select
            value={form.cleaning_type}
            onValueChange={(v) => update("cleaning_type", v)}
          >
            <SelectTrigger id="qf-cleaning-type" data-testid="qf-cleaning-type-trigger">
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent>
              {CLEANING_TYPES.map((c) => (
                <SelectItem key={c.value} value={c.value} data-testid={`qf-type-${c.value}`}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label id="qf-contact-label">Preferred Contact</Label>
          <RadioGroup
            aria-labelledby="qf-contact-label"
            value={form.preferred_contact}
            onValueChange={(v) => update("preferred_contact", v)}
            className="flex gap-3 mt-2"
          >
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value="phone" id="pc-phone" data-testid="qf-contact-phone" />
              <Phone size={14} /> Call
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value="text" id="pc-text" data-testid="qf-contact-text" />
              <MessageSquare size={14} /> Text
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value="email" id="pc-email" data-testid="qf-contact-email" />
              <Mail size={14} /> Email
            </label>
          </RadioGroup>
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="qf-message">Message / Details</Label>
        <Textarea
          id="qf-message"
          rows={4}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Square footage, number of rooms, condition, anything special we should know..."
          data-testid="qf-message"
        />
      </div>

      <div className="mt-4">
        <Label className="block mb-2">Upload Photos (optional)</Label>
        <div className="flex flex-wrap items-center gap-3">
          {photos.map((p, i) => (
            <div
              key={i}
              className="relative h-20 w-20 rounded-xl overflow-hidden border border-slate-200"
              data-testid={`qf-photo-${i}`}
            >
              <img src={p.dataUrl} alt={p.name} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 inline-flex items-center justify-center text-slate-700 shadow"
                aria-label="Remove photo"
                data-testid={`qf-remove-photo-${i}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <label
              className="h-20 w-20 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 cursor-pointer hover:border-[#1E3A8A] hover:text-[#1E3A8A]"
              data-testid="qf-upload-label"
            >
              <Upload size={18} />
              <span className="text-[10px] mt-1">Upload</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFiles}
                className="hidden"
                data-testid="qf-upload-input"
              />
            </label>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Up to 4 photos, 2.5 MB each. Helps us give a more accurate quote.
        </p>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-green w-full md:w-auto mt-6 justify-center disabled:opacity-70"
        data-testid="qf-submit"
      >
        {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        Get My Free Cleaning Quote
      </button>
      <p className="text-xs text-slate-500 mt-3">
        By submitting, you agree to be contacted about your cleaning request. We never share your info.
      </p>
    </form>
  );
}
